import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { content } from '../data/content';
import { X, Menu } from 'lucide-react';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    // Close menu on route change
    useEffect(() => {
        setIsMenuOpen(false);
        document.body.style.overflow = 'unset';
    }, [location.pathname]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        if (!isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
    };

    const scrollToContact = (e) => {
        e.preventDefault();
        setIsMenuOpen(false);
        document.body.style.overflow = 'unset';
        if (location.pathname !== '/') {
            navigate('/', { state: { scrollTo: 'contact' } });
        } else {
            document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const isBlogPath = location.pathname.startsWith('/blog');

    // Hide Navbar on Intranet pages
    if (location.pathname.startsWith('/gnm/admin/intranet')) {
        return null;
    }

    return (
        <>
            <nav className="glass-panel" style={{
                position: 'fixed',
                top: '24px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '90%',
                maxWidth: 'var(--spacing-container)',
                padding: '16px 32px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                zIndex: 1000,
                // Harmonized with site-wide glass panel
                background: 'rgba(15, 23, 42, 0.7)',
                backdropFilter: 'var(--glass-blur) var(--glass-saturate)',
                WebkitBackdropFilter: 'var(--glass-blur) var(--glass-saturate)',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                color: 'white'
            }}>
                <div className="logo">
                    <Link to="/"
                        style={{ display: 'block', cursor: 'pointer' }}
                        onClick={() => {
                            setIsMenuOpen(false);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}>
                        <img
                            src="/brand/logo-white.png"
                            alt="Go Nova"
                            fetchPriority="high"
                            decoding="async"
                            width="200"
                            height="60"
                            style={{
                                height: 'clamp(40px, 5vw, 60px)',
                                width: 'auto',
                                display: 'block',
                                cursor: 'pointer',
                                userSelect: 'none',
                                WebkitUserDrag: 'none'
                            }}
                        />
                    </Link>
                </div>

                {/* Desktop Menu */}
                <div className="nav-links hide-mobile" style={{ display: 'flex', gap: '40px', alignItems: 'center' }}>
                    <ul style={{
                        display: 'flex',
                        gap: '40px',
                        alignItems: 'center',
                        listStyle: 'none',
                        margin: 0,
                        padding: 0
                    }}>
                        <li>
                            <Link to="/about" className={`nav-link ${location.pathname === '/about' ? 'active' : ''}`} style={{
                                color: 'white',
                                fontWeight: 300
                            }}>About Us</Link>
                        </li>
                        <li>
                            <Link to="/services/mcn" className={`nav-link ${location.pathname === '/services/mcn' ? 'active' : ''}`} style={{
                                color: 'white',
                                fontWeight: 300
                            }}>MCN</Link>
                        </li>
                        <li>
                            <Link to="/services/distribution" className={`nav-link ${location.pathname === '/services/distribution' ? 'active' : ''}`} style={{
                                color: 'white',
                                fontWeight: 300
                            }}>Distribution</Link>
                        </li>
                        <li>
                            <Link to="/brands" className={`nav-link ${location.pathname === '/brands' ? 'active' : ''}`} style={{
                                color: 'white',
                                fontWeight: 300
                            }}>Brands</Link>
                        </li>
                        <li>
                            <Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`} style={{
                                color: 'white',
                                fontWeight: 300
                            }}>Contact</Link>
                        </li>
                        <li>
                            <Link to="/blog" className={`nav-link ${location.pathname.startsWith('/blog') ? 'active' : ''}`}>
                                <span style={{
                                    background: 'linear-gradient(135deg, var(--color-accent-primary) 0%, var(--color-accent-secondary) 50%, var(--color-accent-magenta) 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text',
                                    fontWeight: 300,
                                    fontStyle: 'italic',
                                    display: 'inline-block'
                                }}>Blog</span>
                            </Link>
                        </li>
                    </ul>

                    {/* App Store Links */}
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        borderLeft: '1px solid rgba(255,255,255,0.2)',
                        paddingLeft: '24px',
                        marginLeft: '0px',
                        height: '24px',
                        alignItems: 'center'
                    }}>
                        <a href={content.ecosystem.mobile.links.ios} target="_blank" rel="noopener noreferrer" style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.1)',
                            transition: 'background-color 0.3s, color 0.3s, border-color 0.3s'
                        }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = 'black';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                e.currentTarget.style.color = 'white';
                            }}
                            aria-label="Download on App Store">
                            <svg viewBox="0 0 384 512" width="14" height="14" fill="currentColor">
                                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 52.3-11.4 69.5-34.3z" />
                            </svg>
                        </a>
                        <a href={content.ecosystem.mobile.links.android} target="_blank" rel="noopener noreferrer" style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: 'rgba(255,255,255,0.05)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            border: '1px solid rgba(255,255,255,0.1)',
                            transition: 'background-color 0.3s, color 0.3s, border-color 0.3s'
                        }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = 'white';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                            }}
                            aria-label="Get it on Google Play">
                            <svg viewBox="0 0 48 48" width="16" height="16">
                                <path fill="#4db6ac" d="M6.06,42.51c-0.12,0.13-0.08,0.3,0.09,0.38c1.3,0.59,2.83,0.61,4.19-0.03L32,32L6.15,42.45 C6.12,42.46,6.09,42.48,6.06,42.51z" />
                                <path fill="#ffb74d" d="M6.15,5.55C6.09,5.52,6.03,5.49,5.97,5.46C5.81,5.38,5.77,5.2,5.9,5.08c1.33-1.25,3.31-1.35,4.82-0.34L32,16 L6.15,5.55z" />
                                <path fill="#f06292" d="M42.3,18.91C43.51,19.67,44.25,21.03,44.25,22.46c0,1.43-0.74,2.78-1.95,3.54L32,32V16L42.3,18.91z" />
                                <path fill="#4dd0e1" d="M6.31,4.98C5.55,4.9,4.9,5.55,4.9,6.31v35.38c0,0.76,0.65,1.41,1.41,1.33L32,24L6.31,4.98z" />
                            </svg>
                        </a>
                    </div>

                    <a href="https://partner.gonova.media/login" target="_blank" rel="noopener noreferrer" className="glass-panel" style={{
                        padding: '10px 24px',
                        textDecoration: 'none',
                        fontSize: '0.85rem',
                        fontWeight: 500,
                        borderRadius: '50px',
                        background: 'white',
                        color: 'black',
                        border: 'none',
                        transition: 'color var(--transition-fast), border-color var(--transition-fast)' 
                    }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(255,255,255,0.15)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = 'none';
                        }}>
                        Partner Login
                    </a>
                </div>

                <button
                    className="hamburger-simple"
                    onClick={toggleMenu}
                    aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px', color: 'white', zIndex: 1001 }}
                >
                    {isMenuOpen ? <X size={32} strokeWidth={1.5} /> : <Menu size={32} strokeWidth={1.5} />}
                </button>
            </nav>

            {/* Mobile Menu Overlay */}
            <div className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Link to="/about" onClick={() => { setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="mobile-nav-link">About Us</Link>
                    <Link to="/services/mcn" onClick={() => { setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="mobile-nav-link">MCN</Link>
                    <Link to="/services/distribution" onClick={() => { setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="mobile-nav-link">Distribution</Link>
                    <Link to="/brands" onClick={() => { setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="mobile-nav-link">Brands</Link>
                    <Link to="/contact" onClick={() => { setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="mobile-nav-link">Contact</Link>
                    <Link to="/blog" onClick={() => { setIsMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="mobile-nav-link" style={{ borderBottom: 'none' }}>
                         <span style={{ 
                            background: 'linear-gradient(135deg, var(--color-accent-primary) 0%, var(--color-accent-secondary) 50%, var(--color-accent-magenta) 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontWeight: 300,
                            fontStyle: 'italic',
                            display: 'inline-block'
                        }}>Blog</span>
                    </Link>

                    {/* Integrated Action Buttons - No longer at footer */}
                    <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <a href="https://partner.gonova.media/login" target="_blank" rel="noopener noreferrer" style={{
                            padding: '16px',
                            textAlign: 'center',
                            background: 'white',
                            color: 'black',
                            borderRadius: '12px',
                            fontWeight: 800,
                            fontSize: '1rem'
                        }}>
                            Partner Login
                        </a>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                            <a href={content.ecosystem.mobile.links.ios} target="_blank" rel="noopener noreferrer" 
                               style={{ padding: '14px', textAlign: 'center', color: 'white', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', background: 'rgba(255,255,255,0.03)' }}>
                                iOS App
                            </a>
                            <a href={content.ecosystem.mobile.links.android} target="_blank" rel="noopener noreferrer" 
                               style={{ padding: '14px', textAlign: 'center', color: 'white', fontSize: '0.85rem', fontWeight: 600, border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', background: 'rgba(255,255,255,0.03)' }}>
                                Android
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navbar;
